import { AxiosResponse } from 'axios'
import {
  CustomerAgencyModel,
  Guid,
  ApiResult,
  StackHealth,
  CustomerDetailsViewModel,
  LicensedAgencyViewModel,
  EmptyApiResult,
  CustomerDataMigrationViewModel,
  InterfaceHealth,
  CustomerAgencyInterfaceHealthStatuses,
  CustomerDatabaseConfigurationViewModel,
  CustomerDatabaseConfigurationRequestViewModel,
} from '../api'
import { Customer, CustomerSummary } from 'contexts/base'
import { useApiClient } from './useApiClient'
import { createFormDataFromModel } from 'utils'
import { StackHealthPerConnector } from 'utils/health'
import { LicensedAgency } from 'contexts/CurrentCustomerContext'

type BasicResponse = {
  success: boolean
}

export const useCustomerApi = () => {
  const apiClient = useApiClient()

  const getCustomerDetails = (customerAgencyId: Guid) => {
    return apiClient.request<void, AxiosResponse<CustomerDetailsViewModel>>({
      url: `api/customerAgencies/${customerAgencyId}/details`,
      method: 'get',
      withCredentials: true,
    })
  }

  const getCustomerSummary = (customerAgencyId: Guid) => {
    return apiClient.request<void, AxiosResponse<CustomerAgencyModel>>({
      url: `api/customerAgencies/${customerAgencyId}`,
      method: 'get',
      withCredentials: true,
    })
  }

  const getHealthInfo = () => {
    return apiClient.request<void, AxiosResponse<StackHealth>>({
      url: 'api/health',
      method: 'get',
      withCredentials: true,
    })
  }

  const getHealthInfoForAllCustomerAgencies = () => {
    return apiClient.request<
      void,
      AxiosResponse<CustomerAgencyInterfaceHealthStatuses>
    >({
      url: '/api/customerAgencies/health',
      method: 'get',
      withCredentials: true,
    })
  }

  const getHealthInfoForCustomer = (customerAgencyId: string) => {
    return apiClient.request<void, AxiosResponse<StackHealthPerConnector>>({
      url: `/api/health/customer/${customerAgencyId}`,
      method: 'get',
      withCredentials: true,
    })
  }

  const getHealthInfoForCustomerInterface = (
    customerAgencyId: string,
    customerInterfaceId: string,
  ) => {
    return apiClient.request<void, AxiosResponse<InterfaceHealth>>({
      url: `/api/health/interface/${customerAgencyId}/${customerInterfaceId}`,
      method: 'get',
      withCredentials: true,
    })
  }

  const getCustomers = () => {
    return apiClient.request<void, AxiosResponse<CustomerAgencyModel[]>>({
      url: 'api/customerAgencies/Management',
      method: 'get',
      withCredentials: true,
    })
  }

  const refreshData = (customerAgencyId: Guid) => {
    return apiClient.request<void, AxiosResponse<void[]>>({
      url: `api/customerAgencies/${customerAgencyId}/refresh`,
      method: 'get',
      withCredentials: true,
    })
  }

  const editCustomer = (
    customerAgencyId: Guid,
    values: Customer | CustomerSummary,
  ) => {
    const formData = createFormDataFromModel(values, true)

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerAgencyModel>>
    >({
      url: `api/customerAgencies/${customerAgencyId}`,
      method: 'put',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const addCustomer = (values: Customer | CustomerSummary) => {
    const formData = new FormData()
    Object.keys(values).forEach((key: string) => {
      formData.append(key, (values as any)[key])
    })

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerAgencyModel>>
    >({
      url: 'api/customerAgencies',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const deleteCustomer = (customerAgencyId: Guid) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerAgencyModel>>
    >({
      url: `api/customerAgencies/${customerAgencyId}`,
      method: 'delete',
      withCredentials: true,
    })
  }

  const stopAssisting = (customerAgencyId: Guid, licensedAgencyId: Guid) => {
    return apiClient.request<void, AxiosResponse<void>>({
      url: `/api/customerAgencies/${customerAgencyId}/licensedAgencies/${licensedAgencyId}/unassist`,
      method: 'post',
      withCredentials: true,
    })
  }

  const startAssisting = (customerAgencyId: Guid, licensedAgencyId: Guid) => {
    return apiClient.request<void, AxiosResponse<void>>({
      url: `/api/customerAgencies/${customerAgencyId}/licensedAgencies/${licensedAgencyId}/assist`,
      method: 'post',
      withCredentials: true,
    })
  }

  const addLicensedAgency = (
    customerAgencyId: Guid,
    values: LicensedAgencyViewModel,
  ) => {
    const formData = new FormData()
    Object.keys(values).forEach((key: string) => {
      formData.append(key, (values as any)[key])
    })

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<LicensedAgencyViewModel>>
    >({
      url: `api/customerAgencies/${customerAgencyId}/licensedAgencies`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const editLicensedAgency = (
    customerAgencyId: Guid,
    licensedAgencyId: Guid,
    agency: LicensedAgency,
  ) => {
    const formData = createFormDataFromModel(agency)

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<LicensedAgencyViewModel>>
    >({
      url: `api/customerAgencies/${customerAgencyId}/licensedAgencies/${licensedAgencyId}`,
      method: 'put',
      data: formData,
      withCredentials: true,
    })
  }

  const deleteLicensedAgency = (
    customerAgencyId: Guid,
    licensedAgencyId: Guid,
  ) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/customerAgencies/${customerAgencyId}/licensedAgencies/${licensedAgencyId}`,
      method: 'delete',
      withCredentials: true,
    })
  }

  const getCustomerMigrationStatus = (customerAgencyId: Guid) => {
    return apiClient.request<
      void,
      AxiosResponse<CustomerDataMigrationViewModel>
    >({
      url: `api/customerAgencies/${customerAgencyId}/dataMigration`,
      method: 'get',
      withCredentials: true,
    })
  }

  const createCustomerMigration = (customerAgencyId: Guid) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/customerAgencies/${customerAgencyId}/dataMigration/start`,
      method: 'post',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const cancelCustomerMigration = (customerAgencyId: Guid) => {
    return apiClient.request<void, AxiosResponse<EmptyApiResult>>({
      url: `api/customerAgencies/${customerAgencyId}/dataMigration/cancel`,
      method: 'post',
      withCredentials: true,
    })
  }

  const uploadMigrationMappingFile = (customerAgencyId: Guid, file: any) => {
    const formData = new FormData()
    formData.append('file', file)

    return apiClient.request<
      void,
      AxiosResponse<ApiResult<LicensedAgencyViewModel>>
    >({
      url: `api/customerAgencies/${customerAgencyId}/dataMigration/mapping`,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const resetMigrationData = (customerAgencyId: Guid) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<LicensedAgencyViewModel>>
    >({
      url: `api/customerAgencies/${customerAgencyId}/dataMigration/reset`,
      method: 'post',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const migrateLicenses = (customerAgencyId: Guid) => {
    return apiClient.request<void, AxiosResponse<ApiResult<EmptyApiResult>>>({
      url: `api/customerAgencies/${customerAgencyId}/dataMigration/migrateLicenses`,
      method: 'post',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      withCredentials: true,
    })
  }

  const getCustomerDatabaseConfiguration = (customerAgencyId: Guid) => {
    return apiClient.request<
      void,
      AxiosResponse<CustomerDatabaseConfigurationViewModel>
    >({
      url: `api/customerAgencies/${customerAgencyId}/database`,
      method: 'get',
      withCredentials: true,
    })
  }

  const updateCustomerDatabaseConfiguration = (
    customerAgencyId: Guid,
    data: CustomerDatabaseConfigurationRequestViewModel,
  ) => {
    return apiClient.request<
      void,
      AxiosResponse<ApiResult<CustomerDatabaseConfigurationViewModel>>
    >({
      url: `api/customerAgencies/${customerAgencyId}/database`,
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      data,
      withCredentials: true,
    })
  }

  return {
    getHealthInfo,
    getHealthInfoForCustomer,
    getHealthInfoForCustomerInterface,
    getHealthInfoForAllCustomerAgencies,
    getCustomers,
    editCustomer,
    addCustomer,
    deleteCustomer,
    stopAssisting,
    refreshData,
    getCustomerDetails,
    getCustomerSummary,
    startAssisting,
    editLicensedAgency,
    deleteLicensedAgency,
    addLicensedAgency,
    uploadMigrationMappingFile,
    getCustomerMigrationStatus,
    createCustomerMigration,
    cancelCustomerMigration,
    resetMigrationData,
    migrateLicenses,
    getCustomerDatabaseConfiguration,
    updateCustomerDatabaseConfiguration,
  }
}
